/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  Recipient,
  RecipientDataWithPaginated,
} from "../interfaces/reducersInterfaces";
import axios from "axios";
import { MessagesDataWithPagination } from "../../Interfaces/messagesInterface";
import { Label } from "./authSlice";
import { playNotificationSound } from "../../Utils/notificationSound";
import icon192 from "../../assets/192.png";
import icon32 from "../../assets/32.png";

import { handleAxiosError } from "../../Utils/apiUtils";
import { sendRecipientDetails } from "../../Utils/recipientDetailsUtils";

// Get the initial notification state from localStorage
const getBrowserNotificationState = (): boolean => {
  try {
    const savedState = localStorage.getItem("browserNotificationEnabled");
    // Only return true if both the value is 'true' AND permission was previously granted
    return savedState === "true" && Notification.permission === "granted";
      } catch (error) {
    return false;
  }
};

interface MessengerState {
  recipients: RecipientDataWithPaginated;
  messages: MessagesDataWithPagination;
  currentRecipient: Recipient | null;
  loading?: boolean;
  recipientsLoading?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error?: string | any;
  recipientsNextPage: number | null;
  searchKeyword?: string;
  selectedLabels: string[];
  system_user: string;
  isBrowserNotificationOn: boolean;
}

const initialState: MessengerState = {
  recipients: {} as RecipientDataWithPaginated,
  messages: {} as MessagesDataWithPagination,
  currentRecipient: {} as Recipient,
  recipientsNextPage: 1,
  searchKeyword: "",
  selectedLabels: [] as string[],
  system_user: "",
  isBrowserNotificationOn: getBrowserNotificationState(),
};

//Get recipients
export const getRecipients = createAsyncThunk(
  "messenger/getRecipients",
  async (
    {
      page,
      searchKeyword,
      selectedLabels,
      status,
    }: { 
      page: number; 
      searchKeyword?: string; 
      selectedLabels?: string[];
      status?: string;
    },
    { rejectWithValue , getState}
  ) => {
    try {
      const requestParams = {
        page_number: page - 1, // Convert to 0-based indexing
        per_page: 20,
        search: searchKeyword || "",
        labels: selectedLabels || "[]",
        status: status || ""
      };

      const state = getState() as any;
      const token = state.auth.token;

      const response = await axios.get(
        "https://api.insightssystem.com/api:uYdlVwac/web/recipient?",
        {
          params: requestParams,
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: token })
          }
        }
      );

      // Transform the response to match the expected format
      const transformedItems = response.data.map((item: any) => {
        // Determine the last message content
        const lastMessageContent = item.system_user || item.recipient || "";
        
        return {
          recipient: {
            id: item.id,
            created_at: item.created_at,
            user_id: item.user_id,
            name: item.name,
            phone_number: item.phone_number,
            country: item.country,
            global_label: item.global_label ? JSON.parse(item.global_label) : [],
            note: item.note,
            is_ai_assistant: item.is_ai_assistant,
            phone_number_string: item.phone_number_string,
            expiration_timestamp: item.expiration_timestamp,
            last_message_time: item.last_message_time,
            recipient_last_message_time: item.recipient_last_message_time,
            note_auto: item.note_auto
          },
          conversation: {
            message_id: item.message_id || "",
            last_message_id: item.last_message_id || "",
            system_user: item.system_user || "",
            recipient: item.recipient || "",
            read_time: item.read_time || null,
            status: item.status || "read",
            created_at: item.last_message_time || null,
            phone_number: item.phone_number || "",
            user_id: item.user_id || "",
            text: lastMessageContent || ""
          }
        };
      });

      return {
        items: transformedItems,
        itemsReceived: transformedItems.length,
        curPage: page,
        nextPage: response.data.length === 20 ? page + 1 : null,
        prevPage: page > 1 ? page - 1 : null,
        offset: (page - 1) * 20,
        perPage: 20,
        itemsTotal: transformedItems.length, // We don't have total count from API, using current items length
        pageTotal: Math.ceil(transformedItems.length / 20)
      };
    } catch (error) {
      handleAxiosError(error);
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data || "Something went wrong");
      } else {
        return rejectWithValue("An unexpected error occurred");
      }
    }
  }
);

export const getConversation = createAsyncThunk(
  "messenger/getConversation",
  async (
    {
      perPage,
      pageNumber,
      offset,
      phoneNumber,
    }: {
      perPage: number;
      pageNumber: number;
      offset: number;
      phoneNumber: number;
    },
    { rejectWithValue, getState }
  ) => {
    const per_page = perPage;
    const page_number = pageNumber;
    const offsetNumber = offset || 0;
    const phone_number = phoneNumber;

    try {
      const state = getState() as any;
      const token = state.auth.token;
      
      const response = await axios.get(
        `https://api.insightssystem.com/api:uYdlVwac/web/conversations?phone_number=${phone_number}&page_number=${page_number}&per_page=${per_page}&offset=${offsetNumber}`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: token })
          }
        }
      );
      return response.data;
    } catch (error) {
      handleAxiosError(error);
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data || "Something went wrong");
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

export const updateRecipient = createAsyncThunk(
  "messenger/updateRecipient",
  async (
    recipientData: {
      id: number;
      name?: string;
      note?: string;
      country?: string;
      created_at?: number;
      manual_label?: string[];
      global_label?: string[];
      phone_number?: number;
      is_ai_assistant?: boolean;
      phone_number_string?: string;
    },
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState() as any;
      const token = state.auth.token;
      
      const response = await axios.patch(
        `https://api.insightssystem.com/api:uYdlVwac/web/recipient/${recipientData.id}`,
        recipientData,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: token })
          }
        }
      );
      return response.data;
    } catch (error) {
      handleAxiosError(error);
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data || "Something went wrong");
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

//Update message recipient status
export const updateMessageStatus = createAsyncThunk(
  "messenger/updateRecipientMessageStatus",
  async (
    {
      phone_number,
      message_id,
      status,
    }: {
      phone_number?: number | string;
      message_id: string;
      status: string;
    },
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState() as any;
      const token = state.auth.token;
      
      const response = await axios.patch(
        "https://api.insightssystem.com/api:uYdlVwac/web/conversations",
        {
          phone_number,
          status,
          message_id,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: token })
          }
        }
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data || "Something went wrong");
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

interface SendTextMessagePayload {
  text: string;
  currentRecipient: Recipient;
  scheduled_time?: number;
}

export const sendTextMessage = createAsyncThunk(
  "messenger/sendTextMessage",
  async (payload: SendTextMessagePayload, { rejectWithValue, getState }) => {
    

    if (!payload.currentRecipient) {
      return rejectWithValue("Recipient is required");
    }

    try {


      const requestPayload = {
        text: payload.text,
        phone_number: payload.currentRecipient.phone_number,
        ...payload.scheduled_time ? { scheduled_time: payload.scheduled_time } : {}
      };

      const state = getState() as any;
      const token = state.auth.token;
      
      const response = await axios.post(
        "https://api.insightssystem.com/api:uYdlVwac/web/send/text",
        requestPayload,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: token })
          }
        }
      );

      // Send recipient details to API
      await sendRecipientDetails(payload.currentRecipient, token);

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data || "Failed to send message"
        );
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

const messengerSlice = createSlice({
  name: "messenger",
  initialState,
  reducers: {
    newRecipientWithMessage(state, action) {
      const { newRecipient } = action.payload;
      const system_user = state.system_user;

      if (system_user === newRecipient.recipient.user_id) {
        const existingRecipient = state.recipients.items.find(
          (item) =>
            item.recipient.phone_number === newRecipient.recipient.phone_number
        );

        if (!existingRecipient) {
          state.recipients.items.push(newRecipient);
        }
      }
    },
    newMessageByWebhook(state, action) {
      const newMessage = action.payload.message;
      const currentRecipient = action.payload.currentRecipient;
      const labels = action.payload.currentLabels;
      
      // Get the system_user_id from the auth state
      const system_user_id = action.payload.system_user_id;
      


      //notification
      if (
        newMessage.user_id === system_user_id &&
        state.isBrowserNotificationOn &&
        newMessage.recipient &&
        newMessage.phone_number &&
        Notification.permission === "granted"
      ) {
        try {
          new Notification(`New message from +${newMessage.phone_number}`, {
            body: newMessage.recipient,
            tag: `message-${newMessage.phone_number}`,
            requireInteraction: true,
            icon: icon192,
            badge: icon32,
            silent: false,
          });

          playNotificationSound();
        } catch (error) {
          // Error showing notification
        }
      }


      
      if (
        newMessage.user_id === system_user_id &&
        newMessage.phone_number === currentRecipient?.phone_number
      ) {
        const existingMessageIndex = state.messages.items.findIndex(
          (msg) => msg.message_id === newMessage.message_id
        );

        if (existingMessageIndex !== -1) {
          // Update existing message
          state.messages.items[existingMessageIndex] = {
            ...state.messages.items[existingMessageIndex],
            ...newMessage,
          };
        } else {
          // Add new message if it matches the current recipient
          if (newMessage.phone_number === currentRecipient?.phone_number) {
            // Initialize messages.items array if it doesn't exist
            if (!state.messages.items) {
              state.messages.items = [];
            }
            state.messages.items.push(newMessage);
          }
        }
      }

      // First, create a function to handle label matching to avoid code duplication
      const getUpdatedLabels = (
        existingLabels: string[],
        messageText: string,
        labels: Label[]
      ) => {
        const updatedLabels = [...(existingLabels || [])];

        labels.forEach((label: Label) => {
          if (
            label.keywords?.length > 0 &&
            !updatedLabels.includes(label.label)
          ) {
            const hasMatchingKeyword = label.keywords.some(
              (keyword: string) => {
                const regex = new RegExp(`\\b${keyword.toLowerCase()}\\b`);
                return regex.test(messageText.toLowerCase());
              }
            );

            if (hasMatchingKeyword) {
              updatedLabels.push(label.label);
            }
          }
        });

        return updatedLabels;
      };

      // Function to merge global labels
      const mergeGlobalLabels = (existingLabels: string[] = [], newGlobalLabels: string[] = []) => {
        const uniqueLabels = new Set([...existingLabels, ...newGlobalLabels]);
        return Array.from(uniqueLabels);
      };

      // update both currentRecipient and recipients list
      if (
        state.currentRecipient?.user_id === newMessage.user_id &&
        state.currentRecipient?.phone_number === newMessage.phone_number
      ) {
        let updatedCurrentLabels = [...(state.currentRecipient?.global_label || [])];

        // Update labels based on message text if recipient text exists
        if (newMessage.recipient) {
          updatedCurrentLabels = getUpdatedLabels(
            updatedCurrentLabels,
            newMessage.recipient,
            labels
          );
        }

        // Merge with global_label from webhook if it exists
        if (newMessage.recipient_info_of_user?.global_label) {
          updatedCurrentLabels = mergeGlobalLabels(
            updatedCurrentLabels,
            newMessage.recipient_info_of_user.global_label
          );
        }

        // Update currentRecipient with new labels and note_auto
        state.currentRecipient = {
          ...(state.currentRecipient as Recipient),
          last_message_time: newMessage.created_at,
          global_label: updatedCurrentLabels,
          // Only update recipient_last_message_time if it's not a template message conversation
          ...(newMessage.recipient && !state.currentRecipient?._isTemplateMessage && {
            recipient_last_message_time: newMessage.created_at
          }),
          ...(newMessage.recipient_info_of_user?.note_auto !== undefined && {
            note_auto: newMessage.recipient_info_of_user.note_auto
          })
        };
      }

      state.recipients.items = state.recipients.items.map((item) => {
        if (
          item.recipient.phone_number === newMessage.phone_number &&
          newMessage.user_id === system_user_id
        ) {
          const isDifferentMessage =
            item.conversation?.message_id !== newMessage.message_id;

          let updatedGlobalLabels = [...(item.recipient.global_label || [])];

          // Update labels based on message text if it's a different message
          if (isDifferentMessage && newMessage.recipient) {
            updatedGlobalLabels = getUpdatedLabels(
              updatedGlobalLabels,
              newMessage.recipient,
              labels
            );
          }

          // Merge with global_label from webhook if it exists
          if (newMessage.recipient_info_of_user?.global_label) {
            updatedGlobalLabels = mergeGlobalLabels(
              updatedGlobalLabels,
              newMessage.recipient_info_of_user.global_label
            );
          }

          let noteAuto = item.recipient.note_auto;
          if (newMessage.recipient_info_of_user?.note_auto !== undefined) {
            noteAuto = newMessage.recipient_info_of_user.note_auto;
          }

          return {
            ...item,
            conversation: isDifferentMessage
              ? newMessage
              : { ...item.conversation, ...newMessage },
            recipient: {
              ...item.recipient,
              last_message_time: isDifferentMessage
                ? newMessage.created_at
                : item.recipient.last_message_time,
              // Only update recipient_last_message_time if it's not a template message conversation
              recipient_last_message_time: newMessage.recipient && !item.recipient._isTemplateMessage
                ? newMessage.created_at
                : item.recipient.recipient_last_message_time,
              global_label: updatedGlobalLabels,
              note_auto: noteAuto
            },
          };
        }
        return item;
      });
    },
    setCurrentRecipient: (state, action) => {
      state.currentRecipient = action.payload;
    },
    setSearchKeyword: (state, action) => {
      state.searchKeyword = action.payload;
      state.recipientsNextPage = null;
    },
    resetRecipients: (state) => {
      state.recipients = { ...state.recipients, items: [] }; // 🔹 Clear previous data
      state.recipientsNextPage = 1;
      state.searchKeyword = "";
    },
    addSelectedLabel: (state, action) => {
      if (!state.selectedLabels.includes(action.payload)) {
        state.selectedLabels.push(action.payload);
      }
    },
    removeSelectedLabel: (state, action) => {
      state.selectedLabels = state.selectedLabels.filter(
        (label) => label !== action.payload
      );
    },
    clearSelectedLabels: (state) => {
      state.selectedLabels = [];
    },
    setSystemUserToMessenger: (state, action) => {
      state.system_user = action.payload;
    },
    setBrowserNotification: (state, action) => {
      state.isBrowserNotificationOn = action.payload;
      // Persist to localStorage
      localStorage.setItem(
        "browserNotificationEnabled",
        action.payload.toString()
      );
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle getGetRecipients
      .addCase(getRecipients.pending, (state, action) => {
        state.loading = true;
        state.recipientsLoading = true;
        const { searchKeyword, selectedLabels, status } = action.meta.arg;

        // If it's a new search, reset the recipients list
        if (searchKeyword && searchKeyword !== state.searchKeyword) {
          state.recipients = { ...state.recipients, items: [] };
          state.recipientsNextPage = 1;
        }

        // Reset when labels change
        if (selectedLabels) {
          state.recipients = { ...state.recipients, items: [] };
          state.recipientsNextPage = 1;
        }

        // Reset when status filter changes
        if (status !== undefined) {
          state.recipients = { ...state.recipients, items: [] };
          state.recipientsNextPage = 1;
        }
      })
      .addCase(getRecipients.fulfilled, (state, action) => {
        state.loading = false;
        state.recipientsLoading = false;
        const { searchKeyword, selectedLabels, status } = action.meta.arg;

        if (searchKeyword || selectedLabels?.length || status) {
          // Filter mode: Append data just like pagination
          if (state.recipientsNextPage === 1) {
            state.recipients = action.payload;
          } else {
            // Deduplicate recipients by ID to prevent duplicate keys
            const existingIds = new Set(state.recipients.items.map((item: any) => item.recipient.id));
            const newItems = action.payload.items.filter((item: any) => !existingIds.has(item.recipient.id));
            
            state.recipients = {
              ...action.payload,
              items: [...state.recipients.items, ...newItems], // Append filtered results
            };
          }
        } else {
          // Normal mode: Append data on scroll
          if (state.recipientsNextPage === 1) {
            state.recipients = action.payload;
          } else {
            // Deduplicate recipients by ID to prevent duplicate keys
            const existingIds = new Set(state.recipients.items.map((item: any) => item.recipient.id));
            const newItems = action.payload.items.filter((item: any) => !existingIds.has(item.recipient.id));
            
            state.recipients = {
              ...action.payload,
              items: [...state.recipients.items, ...newItems],
            };
          }
        }

        // Update the next page
        state.recipientsNextPage = action.payload.nextPage;
        state.searchKeyword = searchKeyword || "";
        state.selectedLabels = selectedLabels || [];
      })
      .addCase(getRecipients.rejected, (state, action) => {
        state.loading = false;
        state.recipientsLoading = false;
        state.error = action.payload;
      })
      // Handle getConversation
      .addCase(getConversation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getConversation.fulfilled, (state, action) => {
        state.loading = false;
        state.messages = action.payload;
      })
      .addCase(getConversation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch messages";
      })
      //handle update recipient data
      .addCase(updateRecipient.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateRecipient.fulfilled, (state, action) => {
        state.loading = false;
        const recipientIndex = state.recipients.items?.findIndex(
          (item) => item.recipient.id === action.payload.id
        );

        if (recipientIndex !== undefined && recipientIndex !== -1) {
          // Update the specific recipient with the new data
          state.recipients.items[recipientIndex].recipient = {
            ...state.recipients.items[recipientIndex].recipient,
            ...action.payload,
          };

          if (action.payload.id === state.currentRecipient?.id) {
            state.currentRecipient = action.payload;
          }
        } 
      })
      .addCase(updateRecipient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error updating recipient note";
      })
      //handle text message sending
      .addCase(sendTextMessage.pending, (state) => {
        // state.isSending = true;
        state.error = null;
      })
      .addCase(sendTextMessage.fulfilled, (state) => {
        // state.isSending = false;
        state.error = null;
        // state.messages.items.push(action.payload);
        // state.lastMessage = action.payload;
      })
      .addCase(sendTextMessage.rejected, (state, action) => {
        // state.isSending = false;
        state.error = action.payload || "Failed to send message";
      })
      //handle update recipient message status
      .addCase(updateMessageStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateMessageStatus.fulfilled, (state, action) => {
        const { user_id, phone_number, message_id, status } = action.payload;

        // Update messages state
        const messageIndex = state.messages?.items.findIndex(
          (msg) => msg.message_id === message_id
        );
        if (messageIndex !== -1) {
          state.messages.items[messageIndex].status = status;
        }

        // Update recipients.items array
        state.recipients.items = state.recipients.items.map((item) => {
          if (
            item.conversation.user_id === user_id &&
            item.conversation.phone_number === phone_number &&
            item.conversation.message_id === message_id
          ) {
            return {
              ...item,
              conversation: { ...item.conversation, status },
            };
          }
          return item;
        });
      })
      .addCase(updateMessageStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  newRecipientWithMessage,
  newMessageByWebhook,
  setCurrentRecipient,
  setSearchKeyword,
  resetRecipients,
  addSelectedLabel,
  removeSelectedLabel,
  clearSelectedLabels,
  setSystemUserToMessenger,
  setBrowserNotification,
} = messengerSlice.actions;
export const messengerReducer = messengerSlice.reducer;
